import { ComponentChildren, RefObject, createRef, Fragment } from 'preact'
import { useMemo, useCallback } from 'preact/hooks'

import { generateContext } from '../lib/generateContext'
import { sanitize } from '../lib/sanitize'

export const [useFootnotes, FootnotesContext] =
  generateContext<readonly [JSX.Element[], (key: string) => HTMLDataElement | null | undefined]>()

export type Footnote = {
  key: string
  note: string
}

interface FootnotesProviderProps {
  children: ComponentChildren
  footnotes: Record<string, string>
}

export function FootnotesProvider({ footnotes, children }: FootnotesProviderProps) {
  const notes = useMemo(() => new Map<string, RefObject<HTMLDataElement>>(), [])

  const Notes = useMemo((): JSX.Element[] => {
    const sortedFootnotes = Object.entries(footnotes).sort(([a], [z]) => (a < z ? -1 : a > z ? 1 : 0))
    return sortedFootnotes.map(([key, note]) => {
      const ref = createRef<HTMLDataElement>()
      notes.set(key, ref)
      return (
        <Fragment key={key}>
          <dt data-footnote={key} className="pt-1 text-center">
            <a className="text-current no-underline" id={`footnote-${key}`}>
              {key}
            </a>
          </dt>
          <dd
            className="max-w-full px-2 py-1 break-words transition-all ease-out rounded no-highlight"
            ref={ref}
            data-footnote={key}>
            {sanitize(note)}
          </dd>
        </Fragment>
      )
    })
  }, [footnotes, notes])

  const getFootnote = useCallback((key: string) => notes.get(key)?.current, [notes])

  return <FootnotesContext value={[Notes, getFootnote] as const}>{children}</FootnotesContext>
}
